<script setup lang="ts">
import {computed} from 'vue';
import {useRoute} from 'vue-router';
import {useLayout} from '@/layout/composables/layout';
import {useMenuPreferencias} from '@/layout/composables/useMenuPreferencias';
import {itemEhAtivo} from './menu.util';
import type {MenuItemFolha} from './menu.type';

const props = defineProps<{
    item: MenuItemFolha;
    contexto?: string;
    destacado?: boolean;
    // Só a busca rápida (Ctrl+K) exibe a descrição — na sidebar a linha continua compacta.
    mostrarDescricao?: boolean;
}>();

const route = useRoute();
const {layoutState, onMenuToggle} = useLayout();
const {ehFavorito, alternarFavorito} = useMenuPreferencias();

const ativo = computed(() => itemEhAtivo(props.item, route));

const descricao = computed(() => (props.mostrarDescricao ? props.item.descricao : undefined));

const favoritavel = computed(() => props.item.favoritavel !== false);
const favorito = computed(() => ehFavorito(props.item.id));

const aoClicar = (event: Event) => {
    if (props.item.command) {
        props.item.command({originalEvent: event, item: props.item});
    }

    if ((props.item.to || props.item.url) && (layoutState.staticMenuMobileActive || layoutState.overlayMenuActive)) {
        onMenuToggle();
    }
};

const aoFavoritar = () => {
    alternarFavorito(props.item.id);
};
</script>

<template>
    <div class="menu-item" :class="{'menu-item--ativo': ativo, 'menu-item--destacado': destacado, 'menu-item--com-descricao': !!descricao}">
        <router-link v-if="item.to" :to="item.to" class="menu-item__link" tabindex="0" :title="item.label" @click="aoClicar">
            <i :class="item.icon" class="menu-item__icon"></i>
            <span class="menu-item__textos">
                <span class="menu-item__label">{{ item.label }}</span>
                <span v-if="descricao" class="menu-item__descricao">{{ descricao }}</span>
            </span>
            <span v-if="contexto" class="menu-item__contexto">{{ contexto }}</span>
        </router-link>
        <a v-else :href="item.url" :target="item.target" class="menu-item__link" tabindex="0" :title="item.label" @click="aoClicar">
            <i :class="item.icon" class="menu-item__icon"></i>
            <span class="menu-item__textos">
                <span class="menu-item__label">{{ item.label }}</span>
                <span v-if="descricao" class="menu-item__descricao">{{ descricao }}</span>
            </span>
            <span v-if="contexto" class="menu-item__contexto">{{ contexto }}</span>
        </a>
        <button
            v-if="favoritavel"
            type="button"
            class="menu-item__favorito"
            :class="{'menu-item__favorito--ativo': favorito}"
            :aria-pressed="favorito"
            :aria-label="(favorito ? 'Remover dos favoritos' : 'Adicionar aos favoritos') + ': ' + item.label"
            :title="favorito ? 'Remover dos favoritos' : 'Favoritar'"
            @click.stop="aoFavoritar"
        >
            <i class="pi" :class="favorito ? 'pi-star-fill' : 'pi-star'"></i>
        </button>
    </div>
</template>

<style lang="scss" scoped></style>
